import { alpha, Box, IconButton, styled, Switch, Typography, useTheme } from '@mui/material';
import { ChangeEvent, ReactNode } from 'react';

import { Icon, Tooltip } from '@verantos-dev/shared-components';

const StyledIconButton = styled(IconButton)(() => ({
  '&.MuiButtonBase-root.MuiIconButton-root': {
    pointerEvents: 'auto',
    marginLeft: '16px',

    '&:hover ': {
      width: '40px',
      height: '40px',
      backgroundColor: alpha('#d3d4d7', 0.6),
      borderRadius: '4px',
    },
  },
}));

export type CohortBuilderInfoItem = {
  label: string;
  value?: string;
  flex?: number;
};

export type CohortBuilderInfoCardProps = {
  id: string;
  info: CohortBuilderInfoItem[];

  isToggleEnabled?: boolean;
  showTickIcon?: boolean;
  isTickIconEnabled?: boolean;

  tickIconTooltip?: ReactNode;
  toggleTooltip?: ReactNode;
  editTooltip?: ReactNode;
  deleteTooltip?: ReactNode;
  viewButtonTooltip?: ReactNode;

  onToggleChange?: (id: string, state: boolean) => void;
  onEditClick?: (id: string) => void;
  onDeleteClick?: (id: string) => void;
  onViewClick?: (id: string) => void;

  viewMode?: boolean;
  hideEdit?: boolean;
  minimalMode?: boolean;
};

export const CohortBuilderInfoCard = (props: CohortBuilderInfoCardProps) => {
  const {
    id,
    info,

    isToggleEnabled = false,
    showTickIcon = false,
    isTickIconEnabled = false,

    tickIconTooltip = '',
    toggleTooltip = 'Toggle',
    editTooltip = 'Edit',
    deleteTooltip = 'Delete',
    viewButtonTooltip = '',

    onEditClick,
    onDeleteClick,
    onToggleChange,
    onViewClick = onEditClick,

    viewMode = false,
    hideEdit = false,
    minimalMode = false,
  } = props;

  const theme = useTheme();

  const handleToggleClick = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onToggleChange && onToggleChange(id, checked);
  };

  const handleEditClick = () => {
    onEditClick && onEditClick(id);
  };

  const handleDeleteClick = () => {
    onDeleteClick && onDeleteClick(id);
  };

  const handleViewClick = () => {
    onViewClick && onViewClick(id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        height: 80,
        width: '100%',
        backgroundColor: theme.palette.cardBackground?.main,
        px: theme.spacing(6),
        border: minimalMode ? undefined : `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(2),
        boxShadow: 'none',
        boxSizing: 'border-box',
      }}
    >
      {showTickIcon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '24px',
            marginRight: theme.spacing(4),
          }}
        >
          <Tooltip
            id={`cohort-builder-info-card-inUse-tooltip-${id}`}
            arrow
            placement="bottom"
            width="auto"
            title={tickIconTooltip}
          >
            <Icon
              id={`cohort-builder-info-card-inUse-${id}`}
              icon={isTickIconEnabled ? 'inUse' : 'notInUse'}
              width={24}
              height={24}
            />
          </Tooltip>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
        {info.map(({ label, value, flex }) => (
          <Box key={label} sx={{ display: 'flex', flexDirection: 'column', flex: flex ?? 1 }}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {label}
            </Typography>
            <Typography variant="body1" color={theme.palette.text.primary} fontWeight={600}>
              {value ?? '--'}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        {viewMode && !hideEdit ? (
          <Tooltip
            id={'cohort-builder-info-card-view-tooltip'}
            title={viewButtonTooltip}
            placement="top"
            arrow
          >
            <StyledIconButton name={'View'} size="medium" onClick={handleViewClick}>
              <Icon id="cohort-builder-info-card-view-icon" icon="open" width={24} height={24} />
            </StyledIconButton>
          </Tooltip>
        ) : (
          <>
            {onToggleChange && (
              <Tooltip
                id={'cohort-builder-info-card-toggle-tooltip'}
                title={toggleTooltip}
                placement="top"
                arrow
                width={'auto'}
              >
                <Switch
                  id={'cohort-builder-info-card-toggle-switch'}
                  checked={isToggleEnabled}
                  onChange={handleToggleClick}
                  color="primary"
                  size="medium"
                  readOnly={viewMode}
                />
              </Tooltip>
            )}

            {onEditClick && !hideEdit && (
              <Tooltip
                id={'cohort-builder-info-card-edit-tooltip'}
                title={editTooltip}
                placement="top"
                arrow
                width={'auto'}
              >
                <StyledIconButton name={'Edit'} size="medium" onClick={handleEditClick}>
                  <Icon
                    id="cohort-builder-info-card-edit-icon"
                    icon="edit"
                    width={24}
                    height={24}
                  />
                </StyledIconButton>
              </Tooltip>
            )}

            {onDeleteClick && (
              <Tooltip
                id={'cohort-builder-info-card-delete-tooltip'}
                title={deleteTooltip}
                placement="top"
                arrow
                width={'auto'}
              >
                <StyledIconButton name={'Delete'} size="medium" onClick={handleDeleteClick}>
                  <Icon
                    id="cohort-builder-info-card-delete-icon"
                    icon="delete"
                    width={24}
                    height={24}
                  />
                </StyledIconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
